import React from 'react';
import { STORAGE_KEYS } from 'auth/storageKeys';
import { getActiveDineOutletId, getActiveDineOutletName } from 'auth/dineOutlet';
import { usePrintAlerts } from 'hooks/api/usePrintAlerts';
import type { PrintAgentStatus, PrintJob, PrintJobStatus, PrinterHealth } from 'api/types';
import './print.css';

/**
 * Small pieces the three print screens, the department form, the kitchen
 * display and the Bill Queue share: status chips, time formatting, the header
 * outlet, and the print-trouble banner.
 */

export const PRINT_MODULES = { PRINTERS: 'print-printers', AGENTS: 'print-agents', QUEUE: 'print-queue' } as const;

type Tone = 'ok' | 'info' | 'warn' | 'danger' | 'muted';

export const Chip: React.FC<{ tone: Tone; title?: string; children: React.ReactNode; 'data-chip'?: string }> = ({ tone, title, children, ...rest }) => (
  <span className={`prn-chip prn-chip--${tone}`} title={title} data-chip={rest['data-chip']}>{children}</span>
);

export const JOB_STATUS_LABEL: Record<PrintJobStatus, string> = {
  QUEUED: 'Queued', CLAIMED: 'Printing', SENT: 'Sent', PRINTED: 'Printed', FAILED: 'Failed', REROUTED: 'Rerouted', CANCELLED: 'Cancelled',
};
const JOB_TONE: Record<PrintJobStatus, Tone> = {
  QUEUED: 'info', CLAIMED: 'info', SENT: 'ok', PRINTED: 'ok', FAILED: 'danger', REROUTED: 'warn', CANCELLED: 'muted',
};
export const JobStatusChip: React.FC<{ job: Pick<PrintJob, 'jobStatus' | 'lastError' | 'resolvedAt'> }> = ({ job }) => (
  <Chip tone={job.resolvedAt && (job.jobStatus === 'FAILED' || job.jobStatus === 'REROUTED') ? 'muted' : JOB_TONE[job.jobStatus]}
    title={job.lastError} data-chip={job.jobStatus}>
    {JOB_STATUS_LABEL[job.jobStatus] ?? job.jobStatus}{job.resolvedAt ? ' · handled' : ''}
  </Chip>
);

export const AGENT_STATUS_LABEL: Record<PrintAgentStatus, string> = {
  PENDING_APPROVAL: 'Waiting for approval', ACTIVE: 'Active', SUSPENDED: 'Suspended', REVOKED: 'Revoked',
};
const AGENT_TONE: Record<PrintAgentStatus, Tone> = { PENDING_APPROVAL: 'warn', ACTIVE: 'ok', SUSPENDED: 'danger', REVOKED: 'muted' };
export const AgentStatusChip: React.FC<{ status: PrintAgentStatus; reason?: string }> = ({ status, reason }) => (
  <Chip tone={AGENT_TONE[status] ?? 'muted'} title={reason} data-chip={status}>{AGENT_STATUS_LABEL[status] ?? status}</Chip>
);

const HEALTH_TONE: Record<PrinterHealth, Tone> = { ONLINE: 'ok', OFFLINE: 'danger', UNKNOWN: 'muted' };
const HEALTH_LABEL: Record<PrinterHealth, string> = { ONLINE: 'Online', OFFLINE: 'Offline', UNKNOWN: 'Unknown' };
export const HealthChip: React.FC<{ state: PrinterHealth; detail?: string }> = ({ state, detail }) => (
  <Chip tone={HEALTH_TONE[state] ?? 'muted'} title={detail} data-chip={state}>{HEALTH_LABEL[state] ?? state}</Chip>
);

export const OnlineDot: React.FC<{ online: boolean; title?: string }> = ({ online, title }) => (
  <span className={`prn-dot ${online ? 'prn-dot--on' : 'prn-dot--off'}`} title={title ?? (online ? 'Online' : 'Offline')} />
);

/** "12 Oct, 17:42" in the browser's time zone; '—' when absent. */
export const fmtTime = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false });
};

/** "3 min ago", "2 h ago", or the date. */
export const fmtAgo = (iso?: string | null) => {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '—';
  const m = Math.round(ms / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  if (m < 24 * 60) return `${Math.round(m / 60)} h ago`;
  return fmtTime(iso);
};

/** The outlet chosen in the header (this browser tab's): a print job's scope. */
export const currentOutletId = (): string => getActiveDineOutletId();
export const currentOutletName = (): string => getActiveDineOutletName();

/** Whether the signed-in role sees a module (the menu filter's list, from sign-in). */
export const canSeeModule = (moduleId: string): boolean => {
  try {
    const v = JSON.parse(localStorage.getItem(STORAGE_KEYS.RBAC_VISIBLE_MODULES) || '[]');
    return Array.isArray(v) && v.includes(moduleId);
  } catch { return false; }
};

/** The API's own words for a refused call, else the fallback. */
export const apiMessage = (err: any, fallback: string): string => err?.response?.data?.message ?? fallback;

/**
 * The print-trouble banner for the kitchen display and the Bill Queue: tickets
 * that did not print, and agents that are offline. A banner only — no toasts,
 * so a 15 s poll never nags. Renders nothing when all is well, and does not
 * poll /print/alerts at all for a role without the Print Queue screen.
 */
export const PrintAlertBanner: React.FC<{ scopeId?: string | null; onOpenQueue?: () => void }> = ({ scopeId, onOpenQueue }) => {
  const enabled = React.useMemo(() => canSeeModule(PRINT_MODULES.QUEUE), []);
  const { alerts } = usePrintAlerts(scopeId, enabled);
  if (!enabled) return null;
  if (!alerts) return null;
  const failed = alerts.failedJobs.filter((j) => j.jobStatus === 'FAILED').length;
  const rerouted = alerts.failedJobs.filter((j) => j.jobStatus === 'REROUTED').length;
  const offline = alerts.offlineAgents;
  if (!failed && !rerouted && offline.length === 0) return null;
  const s = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
  return (
    <div className={`prn-banner${failed ? '' : ' prn-banner--warn'}`} role="status" data-print-banner>
      {failed > 0 && (
        <span className="prn-banner__item" data-print-banner-failed>
          {s(failed, 'ticket', 'tickets')} not printed
          {onOpenQueue && <> — <button type="button" className="prn-banner__link" onClick={onOpenQueue}>open Print Queue</button></>}
        </span>
      )}
      {rerouted > 0 && (
        <span className="prn-banner__item" data-print-banner-rerouted>
          {s(rerouted, 'ticket', 'tickets')} printed at a backup printer
          {!failed && onOpenQueue && <> — <button type="button" className="prn-banner__link" onClick={onOpenQueue}>open Print Queue</button></>}
        </span>
      )}
      {offline.map((a) => (
        <span key={a.agentId} className="prn-banner__item" data-print-banner-offline
          title={a.lastSeenAt ? `Last seen ${fmtTime(a.lastSeenAt)}` : 'Never seen'}>
          Printing offline at {a.agentName}{a.siteName ? ` (${a.siteName})` : ''}
          {a.waitingJobCount > 0 ? ` · ${s(a.waitingJobCount, 'ticket', 'tickets')} waiting` : ''}
        </span>
      ))}
    </div>
  );
};
